import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { REPORT_PRESETS, type ReportPreset } from '@tailoring-crm/shared';
import { Input, Label, cn } from '@tailoring-crm/ui';

/**
 * The period every report is read over.
 *
 * Presets first and custom last, because nine times out of ten an owner wants "last month" and
 * typing two dates to get it is a small daily tax. The two date inputs appear only once
 * `custom` is chosen, so the common path is one tap and the screen is not carrying a form
 * nobody uses.
 *
 * The presets are the shared list rather than a copy — the server resolves the same names, so a
 * preset that exists here and not there is a compile error rather than a 400 at the counter.
 */

export interface RangePickerValue {
  preset: ReportPreset;
  from?: string | undefined;
  to?: string | undefined;
}

export function RangePicker({
  value,
  onChange,
  idPrefix = 'range',
}: {
  value: RangePickerValue;
  onChange: (next: RangePickerValue) => void;
  idPrefix?: string;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div
        role="radiogroup"
        aria-label={t('reports.period')}
        className="flex flex-wrap gap-1.5 overflow-x-auto"
      >
        {REPORT_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            role="radio"
            aria-checked={value.preset === preset}
            onClick={() => onChange({ ...value, preset })}
            className={cn(
              'shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              value.preset === preset
                ? 'border-transparent bg-primary text-primary-foreground'
                : 'border-border hover:bg-accent',
            )}
          >
            {t(`reports.preset.${preset}`)}
          </button>
        ))}
      </div>

      {value.preset === 'custom' ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-from`}>{t('reports.from')}</Label>
            <Input
              id={`${idPrefix}-from`}
              type="date"
              value={value.from ?? ''}
              className="max-w-44"
              onChange={(event) => onChange({ ...value, from: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-to`}>{t('reports.to')}</Label>
            <Input
              id={`${idPrefix}-to`}
              type="date"
              value={value.to ?? ''}
              className="max-w-44"
              onChange={(event) => onChange({ ...value, to: event.target.value })}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
